import { useAuth } from "react-oidc-context";
import { useMemo } from "react";
import { config } from "../config";
import { decodeAccessTokenClaims, resolveNamespace, resolveRoles, type Claims } from "./namespace";
import { resolveScope } from "./scope";
import { useScopeSelection } from "./scopeContext";
import type { CallerIdentity } from "../api/gateway";

export interface Identity extends CallerIdentity {
  subject: string | null;
  roles: string[];
  /**
   * The operator's first name, for the greeting and the account menu -- the
   * brief asks for the first name, not the username. From the ID token's
   * `given_name` (the `profile` scope is requested), else the first word of
   * `name`, else null. Display only: nothing is authorized on it.
   */
  firstName: string | null;
  /**
   * The login name the person signed in with (owner 2026-10-25: "I could not
   * see using which username I signed"). ZITADEL's `preferred_username`.
   * Display only: nothing is authorized on it.
   */
  username: string | null;
  /**
   * The namespace in the TOKEN. `namespace` is the one the console is acting
   * on: the same, unless the operator has entered one of its providers, in
   * which case it is that provider (a strict descendant -- see auth/scope.ts).
   */
  rootNamespace: string | null;
  /** The provider the operator is inside, or null at their own namespace. */
  provider: string | null;
  /** Enter a provider (a descendant of rootNamespace) or come back (null). */
  enter: (ns: string | null) => void;
  isAuthenticated: boolean;
}

export function firstNameOf(claims: Claims | null | undefined): string | null {
  const given = claims?.["given_name"];
  if (typeof given === "string" && given.trim()) return given.trim();
  const name = claims?.["name"];
  if (typeof name === "string" && name.trim()) return name.trim().split(/\s+/)[0] ?? null;
  return null;
}

export function usernameOf(claims: Claims | null | undefined): string | null {
  const u = claims?.["preferred_username"];
  return typeof u === "string" && u.trim() ? u.trim() : null;
}

/**
 * The single place the app learns who it is acting as.
 *
 * 🔑 Namespace and roles are read from the TOKEN's claims. The only override
 * is entering one of the token's own providers -- a strict descendant, which
 * the gateway would let this token act on by name anyway -- and a stored
 * selection outside that bound resolves to the root, not to itself. A tenant
 * the UI could freely choose would be a tenant an attacker could choose; a
 * descendant of your own is not a new tenant.
 */
export function useIdentity(): Identity {
  const auth = useAuth();
  const { selected, select } = useScopeSelection();
  return useMemo(() => {
    const idClaims = auth.user?.profile as Claims | undefined;
    const token = auth.user?.access_token ?? null;
    // 🔑 Entitlement is read from the token we SEND, not the one we were
    // identified by. See decodeAccessTokenClaims -- ZITADEL asserts roles into
    // the access token for the requested audience, and into the ID token only
    // if the project is configured to. Reading the wrong one makes the screen
    // disagree with the backend about what this operator may do.
    const accessClaims = decodeAccessTokenClaims(token);
    const projectId = config().oidc.projectId;

    const roles = [
      ...new Set([
        ...resolveRoles(accessClaims, projectId),
        ...resolveRoles(idClaims, projectId),
      ]),
    ];

    const root = resolveNamespace(accessClaims) ?? resolveNamespace(idClaims);
    const namespace = resolveScope(root, selected);

    return {
      token,
      namespace,
      rootNamespace: root,
      provider: namespace !== null && namespace !== root ? namespace : null,
      enter: select,
      subject:
        (accessClaims?.["sub"] as string | undefined) ??
        (idClaims?.["sub"] as string | undefined) ??
        null,
      roles,
      firstName: firstNameOf(idClaims) ?? firstNameOf(accessClaims),
      username: usernameOf(idClaims) ?? usernameOf(accessClaims),
      isAuthenticated: auth.isAuthenticated,
    };
  }, [auth.user, auth.isAuthenticated, selected, select]);
}
